import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Trash2 } from "lucide-react";

// Super Admin only — removes a catalog item (CAT #). Type-the-CAT# confirmation + reason; item archived server-side.
export function DeleteCatalogButton({ row, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const { data: impact } = useQuery({
    queryKey: ["inventory-delete-impact", row?.id],
    queryFn: async () => (await api.get(`/inventory/${row.id}/delete-impact`)).data,
    enabled: open && !!row?.id,
  });
  const ready = confirm.trim().toUpperCase() === String(row?.cat_number || "").toUpperCase() && reason.trim().length >= 3;
  const doDelete = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/inventory/${row.id}`, { data: { confirm_cat: confirm.trim(), reason: reason.trim() } });
      toast.success(`CAT # ${data.cat_number} deleted from the catalog${data.orders_affected ? ` · ${data.orders_affected} order(s) still show the number as text` : ""}`);
      setOpen(false);
      onDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete catalog item");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Button type="button" variant="outline" className="mr-auto gap-1 border-red-300 text-red-700 hover:bg-red-50" onClick={() => { setConfirm(""); setReason(""); setOpen(true); }} data-testid="delete-catalog-btn">
        <Trash2 size={14} /> Delete catalog #
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="delete-catalog-dialog">
          <DialogHeader><DialogTitle className="text-red-800">Delete catalog # {row?.cat_number}?</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-red-900">
              <div className="font-semibold">This removes the catalog item from Inventory.</div>
              <ul className="list-disc ml-5 mt-1 text-xs space-y-0.5">
                <li>{impact ? `${impact.orders_affected} order(s) reference ${impact.cat_number}` : "Checking orders…"}{impact?.orders_sample?.length ? ` — ${impact.orders_sample.join(", ")}${impact.orders_affected > impact.orders_sample.length ? "…" : ""}` : ""}. They keep the number as text but lose stock / code matching.</li>
                <li>{impact ? `Stock on the shelf: ${impact.stock} set(s)` : ""}{impact?.stock > 0 ? " — this stock is dropped from the inventory totals." : ""}</li>
                <li>A full copy is archived with your name and reason (Super Admin audit).</li>
              </ul>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-widest">Type the CAT # to confirm</label>
              <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={row?.cat_number} className="mt-1 font-mono" data-testid="delete-catalog-confirm" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-widest">Reason</label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why this catalog # is removed" className="mt-1" data-testid="delete-catalog-reason" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="delete-catalog-cancel">Cancel</Button>
            <Button className="bg-red-700 hover:bg-red-800 text-white gap-1" disabled={!ready || busy} onClick={doDelete} data-testid="delete-catalog-confirm-btn">
              <Trash2 size={14} /> {busy ? "Deleting…" : "Delete permanently"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
